import {
  CountryClassificationEnum,
  EntryTermEnum,
  FafsaCompletionStatusEnum,
  GenderEnum,
  GoingStatusEnum,
  JoinTypeEnum,
  PredictionIndexEnum,
  StudentTypeEnum,
  YesNoEnum,
} from "@/__generated__/types";
import { getGroupNames } from "@/util/funnelGroups";
import { getJoinStateGroupNames } from "@/util/joinStateGroups";
import { FilterGroup, FilterInputData, FilterName } from "./types";

export type OptionLookup = Record<FilterName, Record<string, string>>;

export function buildOptionLookup(
  filterGroups: FilterGroup[] | null,
): OptionLookup {
  const lookup = {} as OptionLookup;
  if (!filterGroups) return lookup;

  for (const group of filterGroups) {
    for (const filter of group.filters) {
      if (filter.options) {
        lookup[filter.name] = Object.fromEntries(
          filter.options.map((opt) => [opt.value, opt.label]),
        );
      }
    }
  }
  return lookup;
}

// Build analytics properties manually, so the property names remain stable
export function buildFilterAnalyticsProperties(
  data: FilterInputData,
  lookup: OptionLookup,
  hasRise: boolean,
): Record<string, boolean | string | string[]> {
  const props: Record<string, boolean | string | string[]> = {};

  const getLabels = (name: FilterName, values?: string[]) =>
    values?.map((v) => lookup[name]?.[v] ?? v);

  // Cohort Year - only include selected years as true
  for (const year of data.cohortYear ?? []) {
    props[`Cohort Year - ${year}`] = true;
  }

  // Student Type
  for (const type of Object.values(StudentTypeEnum)) {
    props[`Student Type - ${type}`] = data.studentType === type;
  }

  // Funnel State
  for (const key of getGroupNames("SIDEBAR")) {
    props[`Funnel State - ${key}`] = data.funnelStates?.includes(key) ?? false;
  }

  // Assigned Counselor - array of labels
  const counselorLabels = getLabels(
    "assignedCounselors",
    data.assignedCounselors,
  );
  if (counselorLabels?.length) props["Assigned Counselor"] = counselorLabels;

  // Commit Index
  for (const index of Object.values(PredictionIndexEnum)) {
    props[`Commit Index - ${index}`] =
      data.commitIndex?.includes(index) ?? false;
  }

  // Interests - array of labels
  const interestLabels = getLabels("interests", data.interests);
  if (interestLabels?.length) props["Interests"] = interestLabels;

  // Major - array of labels
  const majorLabels = getLabels("majors", data.majors);
  if (majorLabels?.length) props["Major"] = majorLabels;

  // Going Status
  for (const status of Object.values(GoingStatusEnum)) {
    props[`Going Status - ${status}`] =
      data.goingStatus?.includes(status) ?? false;
  }

  // Going Elsewhere
  props["Going Elsewhere - Yes"] = data.goingElsewhere === YesNoEnum.Yes;
  props["Going Elsewhere - No"] = data.goingElsewhere === YesNoEnum.No;

  // Attended a Live Event
  props["Attended a Live Event - Yes"] =
    data.attendedALiveEvent === YesNoEnum.Yes;
  props["Attended a Live Event - No"] =
    data.attendedALiveEvent === YesNoEnum.No;

  // Join Date
  props["Join Date"] = !!(data.joinDate?.from || data.joinDate?.to);

  // Join Type
  for (const type of Object.values(JoinTypeEnum)) {
    props[`Join Type - ${type}`] = data.joinType?.includes(type) ?? false;
  }

  // Join State
  for (const key of getJoinStateGroupNames()) {
    props[`Join State - ${key}`] = data.joinState?.includes(key) ?? false;
  }

  // Gender
  for (const gender of Object.values(GenderEnum)) {
    props[`Gender - ${gender}`] = data.gender?.includes(gender) ?? false;
  }

  // Country
  for (const country of Object.values(CountryClassificationEnum)) {
    props[`Country - ${country}`] = data.country === country;
  }

  // State - array of labels
  const stateLabels = getLabels("state", data.state);
  if (stateLabels?.length) props["State"] = stateLabels;

  // Zip Code - array of values
  if (data.zipCode?.length) props["Zip Code"] = data.zipCode;

  // Direct Admissions
  props["Direct Admissions - Yes"] = data.directAdmission === YesNoEnum.Yes;
  props["Direct Admissions - No"] = data.directAdmission === YesNoEnum.No;

  // Deposited
  props["Deposited - Yes"] = data.deposited === YesNoEnum.Yes;
  props["Deposited - No"] = data.deposited === YesNoEnum.No;

  // First Generation
  props["First Generation - Yes"] = data.firstGeneration === YesNoEnum.Yes;
  props["First Generation - No"] = data.firstGeneration === YesNoEnum.No;

  // Student Athlete
  props["Student Athlete - Yes"] = data.studentAthlete === YesNoEnum.Yes;
  props["Student Athlete - No"] = data.studentAthlete === YesNoEnum.No;

  // Campus Visit
  props["Campus Visit - Yes"] = data.campusVisit === YesNoEnum.Yes;
  props["Campus Visit - No"] = data.campusVisit === YesNoEnum.No;

  // Lead Source - array of values
  if (data.leadSource?.length) props["Lead Source"] = data.leadSource;

  // Entry Term
  for (const term of Object.values(EntryTermEnum)) {
    props[`Entry Term - ${term}`] = data.entryTerm?.includes(term) ?? false;
  }

  // Completed FAFSA
  for (const status of Object.values(FafsaCompletionStatusEnum)) {
    props[`Completed FAFSA - ${status}`] =
      data.fafsaCompletionStatus?.includes(status) ?? false;
  }

  // CEEB code - array of values
  if (data.ceebCode?.length) props["CEEB code"] = data.ceebCode;

  // RISE filters - only include if RISE is enabled
  if (hasRise) {
    props["More Active Elsewhere - Yes"] =
      data.moreActiveElsewhere === YesNoEnum.Yes;
    props["More Active Elsewhere - No"] =
      data.moreActiveElsewhere === YesNoEnum.No;

    const applyIndexLabels = getLabels("applyIndex", data.applyIndex);
    if (applyIndexLabels?.length) props["Rise Apply Index"] = applyIndexLabels;

    const commitIndexLabels = getLabels(
      "riseCommitIndex",
      data.riseCommitIndex,
    );
    if (commitIndexLabels?.length)
      props["Rise Commit Index"] = commitIndexLabels;

    const meltIndexLabels = getLabels("meltIndex", data.meltIndex);
    if (meltIndexLabels?.length) props["Rise Melt Index"] = meltIndexLabels;

    const yieldIndexLabels = getLabels("yieldIndex", data.yieldIndex);
    if (yieldIndexLabels?.length) props["Rise Yield Index"] = yieldIndexLabels;
  }

  return props;
}
